import { usePricingRights } from '@util/PricingRightsHook';
import React from 'react';
import Utilities from '@util/Utilities';
import FormButton from '@app/component/form/button/FormButton';
import {
	AdjustmentsHorizontalIcon,
	ArrowTrendingUpIcon,
	BellAlertIcon,
	CalendarDaysIcon,
	ChartBarIcon,
	PresentationChartBarIcon,
	SignalIcon,
	TableCellsIcon,
	TagIcon,
	ArrowsRightLeftIcon,
	NumberedListIcon,
} from '@heroicons/react/24/outline';
import { CogIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import SecuredFragment from '@app/component/security/SecuredFragment';
import Authority from '@app/model/authentication/Authority';
import { Tooltip } from 'react-tooltip';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import Message from '@app/component/message/Message';
import { Link, matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SquaresPlusIcon } from '@heroicons/react/24/solid';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import DataProvider from '@app/model/_common/DataProvider';
import {
	MarketStateFilterInputProvider,
} from '@app/site/secured/rateshopper/rates/provider/MarketStateFilterInputProvider';
import CompanyHotelParsingSettingState from '@app/model/company_hotel/CompanyHotelParsingSettingState';
import ButtonLinkList from '@app/component/button-link-list/ButtonLinkList';
import { channelCheckStayLengths } from '@app/site/secured/rateshopper/rates/channel-check/ChannelCheckStayLengths';
import { useBookingScanCity } from '@app/site/secured/rateshopper/rates/booking-ranking/service/useBookingScanCity';

const RateshopperDashboardPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	if (!!companyHotel) {
		return <MarketStateFilterInputProvider companyHotel={companyHotel}>
			<Dashboard companyHotel={companyHotel} />
		</MarketStateFilterInputProvider>;
	}

	return <Message state="warning">Please refresh the page</Message>;

});

const Dashboard: React.FunctionComponent<{ companyHotel: CompanyHotel }>
	= ({ companyHotel }) => {
	const { canChangePrices } = usePricingRights(companyHotel);

	const navigate = useNavigate();
	const location = useLocation();

	const isCalendar = !!matchPath('/secured/rateshopper/rates/market/calendar', location.pathname);
	const isTable = !!matchPath('/secured/rateshopper/rates/market/table', location.pathname);
	const isGraph = !!matchPath('/secured/rateshopper/rates/market/graph', location.pathname);
	const isParity = !!matchPath('/secured/rateshopper/rates/parity', location.pathname);
	const isChannelCheck = !!matchPath('/secured/rateshopper/rates/channels', location.pathname);
	const isAlerts = !!matchPath('/secured/rateshopper/rates/alerts', location.pathname);
	const isMarketPulse = !!matchPath('/secured/rateshopper/rates/market-pulse', location.pathname);
	const isMetaradar = !!matchPath('/secured/rateshopper/rates/metaradar', location.pathname);
	const isSettings = !!matchPath('/secured/rateshopper/rates/settings', location.pathname);
	const isRoomMapping = !!matchPath('/secured/rateshopper/rates/room-mapping', location.pathname);
	const isFlexiSets = !!matchPath('/secured/rateshopper/rates/flexi-sets/*', location.pathname);
	const isPricingAutomation = !!matchPath('/secured/rateshopper/rates/pricing-automation/*', location.pathname);
	const isPromotions = !!matchPath('/secured/rateshopper/rates/promotions', location.pathname);
	const isBookingRanking = !!matchPath('/secured/rateshopper/rates/booking-ranking/*', location.pathname);
	// The Booking ranking button shows only for a hotel in an active Scan city (decision D6).
	const { isActive: hasBookingRanking } = useBookingScanCity(companyHotel.id);
	const hasChannelCheck = channelCheckStayLengths(companyHotel).length > 0;

	if (!!companyHotel) {

		const hasBrandCom = companyHotel.parsingSettings?.some(s => s.variables.provider === DataProvider.BRAND_COM && s.state === CompanyHotelParsingSettingState.ACTIVE);

		return <>
			<header className="bg-white shadow-sm">
				<div className="mx-auto max-w-7xl py-4 px-4 sm:px-6 lg:px-8">
					<div className="md:flex md:items-center md:justify-between">
						<div className="min-w-0 flex-1">
							<h1 className="text-lg font-semibold leading-6 text-gray-900">
								RateShopper
							</h1>
						</div>
						<div className="md:hidden">
							<div className="mt-1">
								<select aria-label="Select a tab" className="w-full input"
												onChange={(e) => navigate(e.target.value)}>
									<option value="/secured/rateshopper/rates/market/calendar" selected={isCalendar}>Dashboard</option>
									<option value="/secured/rateshopper/rates/market/table" selected={isTable}>Rates Table View</option>
									{hasBrandCom && companyHotel.hasParityModule() &&
										<option value="/secured/rateshopper/rates/parity" selected={isParity}>Parity</option>}
									{hasChannelCheck &&
										<option value="/secured/rateshopper/rates/channels" selected={isChannelCheck}>Channel check</option>}
									<option value="/secured/rateshopper/rates/alerts" selected={isAlerts}>Alerts & Reports</option>
									{!!companyHotel.settings.main.marketAreaId && companyHotel.hasMarketInsightModule() &&
										<option value="/secured/rateshopper/rates/market-pulse" selected={isMarketPulse}>Market</option>}
									<option value="/secured/rateshopper/rates/metaradar" selected={isMetaradar}>Metaradar</option>
									{hasBookingRanking &&
										<option value="/secured/rateshopper/rates/booking-ranking" selected={isBookingRanking}>Booking ranking</option>}
									<option value="/secured/rateshopper/rates/settings" selected={isSettings}>Settings</option>
									<option value="/secured/rateshopper/room-mapping" selected={isRoomMapping}>Room Mapping</option>
									{companyHotel.hasFlexiSetsModule() &&
										<option value="/secured/rateshopper/rates/flexi-sets" selected={isFlexiSets}>FlexiSets</option>}
									{canChangePrices &&
										<option value="/secured/rateshopper/rates/pricing-automation"
												selected={isPricingAutomation}>Automation</option>}
									<option value="/secured/rateshopper/rates/promotions" selected={isPromotions}>Promotions</option>
								</select>
							</div>
						</div>
						<div className="hidden md:block">
							<div className="mt-4 md:flex space-y-2 md:space-y-0 md:space-x-2.5 md:ml-4 md:mt-0">

								<div className="isolate inline-flex rounded-md shadow-sm mr-3">
									<Link data-tooltip-id="button-tooltip" data-tooltip-content="Calendar"
												type="button" to="/secured/rateshopper/rates/market/calendar"
												className={Utilities.classNames('relative inline-flex items-center rounded-l-md px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-10', isCalendar ? ' text-blue-700 bg-blue-50' : 'text-gray-900 hover:bg-gray-50')}
									>
										<CalendarDaysIcon className="w-5 h-5" />
									</Link>
									<Link data-tooltip-id="button-tooltip" data-tooltip-content="Table"
												type="button"
												to="/secured/rateshopper/rates/market/table"
												className={Utilities.classNames('relative -ml-px inline-flex items-center px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-10', isTable ? ' text-blue-700 bg-blue-50' : 'text-gray-900 hover:bg-gray-50')}
									>
										<TableCellsIcon className="w-5 h-5" />
									</Link>
									<Link data-tooltip-id="button-tooltip" data-tooltip-content="Graph"
												to="/secured/rateshopper/rates/market/graph"
												type="button"
												className={Utilities.classNames('relative -ml-px inline-flex items-center rounded-r-md px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-10', isGraph ? ' text-blue-700 bg-blue-50' : '')}
									>
										<ChartBarIcon className="w-5 h-5" />
									</Link>
									<Tooltip id="button-tooltip" />
								</div>

								{companyHotel.hasParityModule() && <div data-tooltip-id="parity-tooltip"
																												data-tooltip-content="You need to activate Brand.com!">
									<FormButton state="white" isLink to="/secured/rateshopper/rates/parity" disabled={!hasBrandCom}
															extraClassName={Utilities.classNames('font-normal py-1.5', isParity ? ' text-blue-700 bg-blue-50' : '')}>
										<PresentationChartBarIcon className="w-4 h-4 mr-2 mt-0.5" />
										Parity
									</FormButton>
								</div>}

								<div data-tooltip-id="metaradar-tooltip"
											 data-tooltip-content="Metaradar">
									<FormButton state="white" isLink to="/secured/rateshopper/rates/metaradar"
															extraClassName={Utilities.classNames('font-normal py-1.5', isMetaradar ? ' text-blue-700 bg-blue-50' : '')}>
										<SignalIcon className="w-4 h-4 mr-2" />
										Metaradar
									</FormButton>
								</div>
								<div data-tooltip-id="market-pulse-tooltip">
									<FormButton state="white" isLink to="/secured/rateshopper/rates/market-pulse"
															disabled={!companyHotel.settings.main.marketAreaId || !companyHotel.hasMarketInsightModule()}
															extraClassName={Utilities.classNames('font-normal py-1.5', isMarketPulse ? ' text-blue-700 bg-blue-50' : '')}>
										<ArrowTrendingUpIcon className="w-4 h-4 mr-2" />
										MarketPulse
									</FormButton>
								</div>
								{hasBookingRanking && <div className="inline-flex items-center" data-tooltip-id="booking-ranking-tooltip" data-tooltip-content="Booking ranking">
									<FormButton state="white" isLink to="/secured/rateshopper/rates/booking-ranking"
															extraClassName={Utilities.classNames('font-normal py-1.5', isBookingRanking ? ' text-blue-700 bg-blue-50' : '')}>
										<NumberedListIcon className="w-4 h-4 mr-2" />
										Booking ranking
									</FormButton>
								</div>}
								<ButtonLinkList items={[
									{
										name: 'Automation',
										disabled: !canChangePrices,
										link: '/secured/rateshopper/rates/pricing-automation',
										icon: <AdjustmentsHorizontalIcon className="w-4 h-4" />,
										isSelected: isPricingAutomation,
									},
									{
										name: 'FlexiSets',
										disabled: !companyHotel.hasFlexiSetsModule(),
										link: '/secured/rateshopper/rates/flexi-sets',
										icon: <SquaresPlusIcon className="w-4 h-4" />,
										isSelected: isFlexiSets,
									},
									...(hasChannelCheck ? [{
										name: 'Channel check',
										link: '/secured/rateshopper/rates/channels',
										icon: <ArrowsRightLeftIcon className="w-4 h-4" />,
										isSelected: isChannelCheck,
									}] : []),
									{
										name: 'Promotions',
										link: '/secured/rateshopper/rates/promotions',
										icon: <TagIcon className="h-4 w-4" />,
										isSelected: isPromotions,
										showSelectedCheck: false,
										badge: <span className="ml-auto rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700">New</span>,
									},
								]} hideArrow extraButtonClassName="btn btn-white font-normal py-1.5 h-9 px-3 mr-3 relative"
																menuItemClassName="block px-4 py-2 text-sm relative">
									<EllipsisVerticalIcon className="h-3.5 w-3.5 flex-shrink-0" />
									{(isChannelCheck || isFlexiSets || isPricingAutomation || isPromotions) && <span
										className="absolute inline-flex rounded-full h-1.5 w-1.5 bg-green-500 right-1.5 top-1.5"></span>}
								</ButtonLinkList>
								<div className="isolate inline-flex rounded-md shadow-sm">

									<SecuredFragment hasAnyAuthority={[Authority.ADMIN, Authority.USER, Authority.OWNER]}>
										<Link to="/secured/rateshopper/rates/alerts" data-tooltip-id="alerts-tooltip"
													data-tooltip-content="Alerts"
													className={Utilities.classNames('relative inline-flex items-center rounded-l-md  px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10', isAlerts ? ' text-blue-700 bg-blue-50' : 'text-gray-900 bg-white')}>
											<BellAlertIcon className="w-5 h-5" />
										</Link>
									</SecuredFragment>
									{/* Settings */}

									<Link to="/secured/rateshopper/rates/settings" data-tooltip-id="settings-tooltip"
												data-tooltip-content="Dashboard Settings"
												className={Utilities.classNames('relative inline-flex items-center -ml-px rounded-r-md px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10', !companyHotel.hasRatesModule() ? 'rounded-l-md' : '', isSettings ? ' text-blue-700 bg-blue-50' : 'text-gray-900 bg-white')}>
										<CogIcon className="w-5 h-5" />
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
				<Tooltip id="coming-soon-tooltip" />
				<Tooltip id="alerts-tooltip" />
				<Tooltip id="settings-tooltip" />
				<Tooltip id="room-mapping-tooltip" />
				<Tooltip id="market-pulse-tooltip">
					MarketPulse
				</Tooltip>
				<Tooltip id="metaradar-tooltip" />
				<Tooltip id="booking-ranking-tooltip" />
				{!hasBrandCom && <Tooltip id="parity-tooltip" />}
			</header>
			<Outlet />
		</>;

	}
	return <Message state="error"
									title="There was an error showing the dashboard!. Please try again later or contact support!" />;
};


export default RateshopperDashboardPage;
